// components/members/MembersCarouselSection.js
import React from 'react';
import { Carousel, Button, Spinner } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import MemberCard from './MemberCard';
import useMemberData from '../../hooks/useMemberData';
import { MEMBER_TYPES } from '../../utils/membersConfig';

// Helper function to get defaults based on member type
const getDefaults = (memberType) => {
  switch (memberType) {
    case MEMBER_TYPES.SENATE:
      return {
        title: 'U.S. Senators',
        viewAllLink: '/us/senators',
        viewAllText: 'View All Senators',
      };
    case MEMBER_TYPES.PRESIDENT:
      return {
        title: 'U.S. Presidents',
        viewAllLink: '/us/presidents',
        viewAllText: 'View All Presidents',
      };
    case MEMBER_TYPES.VICE_PRESIDENT:
      return {
        title: 'U.S. Vice Presidents',
        viewAllLink: '/us/vicepresidents',
        viewAllText: 'View All Vice Presidents',
      };
    case MEMBER_TYPES.JUSTICE:
      return {
        title: 'Supreme Court Justices',
        viewAllLink: '/us/justices',
        viewAllText: 'View All Justices',
      };
    case MEMBER_TYPES.HOUSE:
    default:
      return {
        title: 'U.S. Representatives',
        viewAllLink: '/us/houseofreps',
        viewAllText: 'View All Representatives',
      };
  }
};

/**
 * MembersCarouselSection - Displays members in a React Bootstrap Carousel (auto-rotating)
 * Matches the pattern used by DepartmentCarousel in MultiDepartmentSection
 * 
 * @param {string} memberType - MEMBER_TYPES.HOUSE, MEMBER_TYPES.SENATE, MEMBER_TYPES.PRESIDENT, etc.
 * @param {number} limit - Number of members to load (default: 10)
 * @param {string} title - Section title
 * @param {React.Element} icon - Icon component to display before title
 * @param {string} viewAllLink - Link to view all members
 * @param {string} viewAllText - Text for view all button
 * @param {number} carouselInterval - Interval between slides in ms (default: 5000)
 * @param {boolean} showViewAll - Whether to show view all slide (default: true)
 */
const MembersCarouselSection = ({
  memberType = MEMBER_TYPES.HOUSE,
  limit = 10,
  title,
  icon,
  viewAllLink,
  viewAllText,
  carouselInterval = 5000,
  showViewAll = true
}) => {
  const navigate = useNavigate();
  const { data, loading, error } = useMemberData(memberType, limit);

  // Get default values based on member type
  const defaults = getDefaults(memberType);
  const displayTitle = title || defaults.title;
  const displayViewAllLink = viewAllLink || defaults.viewAllLink;
  const displayViewAllText = viewAllText || defaults.viewAllText;

  // Handle title click
  const handleTitleClick = (e) => {
    e.preventDefault();
    if (displayViewAllLink) {
      navigate(displayViewAllLink);
    }
  };

  // Reusable heading component matching DeskCarousel style
  const SectionHeading = () => (
    <div id="homePageBranchRow">
      <h4 id="branchHeading" onClick={handleTitleClick} style={{ cursor: 'pointer' }}>
        {icon}
        <b id="branchHeadingBold">{displayTitle}</b>
        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" className="bi bi-chevron-right" viewBox="0 0 16 16" id="clickChevron">
          <path fillRule="evenodd" d="M4.646 1.646a.5.5 0 0 1 .708 0l6 6a.5.5 0 0 1 0 .708l-6 6a.5.5 0 0 1-.708-.708L10.293 8 4.646 2.354a.5.5 0 0 1 0-.708z"/>
        </svg>
      </h4>
    </div>
  );

  // Loading state
  if (loading) {
    return (
      <div className="section-carousel mb-3">
        <SectionHeading />
        <div style={{ 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center', 
          height: '200px' 
        }}>
          <Spinner animation="grow" variant="light" size='sm' />
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="section-carousel mb-3">
        <SectionHeading />
        <div style={{ 
          color: 'white', 
          padding: '20px', 
          textAlign: 'center' 
        }}>
          Error loading members
        </div>
      </div>
    );
  }

  // Empty state
  if (!data || data.length === 0) {
    return (
      <div className="section-carousel mb-3">
        <SectionHeading />
        <div style={{ 
          color: 'white', 
          padding: '20px', 
          textAlign: 'center' 
        }}>
          No members available
        </div>
      </div>
    );
  }

  return (
    <div className="section-carousel mb-3">
      <SectionHeading />

      <Carousel id='carouselInColumn' controls={false} interval={carouselInterval}>
        {/* Member Items */}
        {data.map((member, index) => (
          <Carousel.Item 
            key={member.bio_guide_id || member.president_num || member.full_name || index} 
            interval={carouselInterval}
          >
            <MemberCard
              member={member}
              memberType={memberType}
              variant="full"
              containerId="feedContainerNormalView"
            />
          </Carousel.Item>
        ))}

        {/* View All Item */}
        {showViewAll && (
          <Carousel.Item interval={carouselInterval}>
            <div id='legislativeConInCol'>
              <div id='viewFullPageButton'>
                <Button 
                  id='viewFullLegislativePageButtonMobile' 
                  size='lg' 
                  href={displayViewAllLink}
                >
                  {displayViewAllText}
                </Button>
              </div>
            </div>
          </Carousel.Item>
        )}
      </Carousel>
    </div>
  );
};

export default MembersCarouselSection;